<template>
	<v-dialog v-model="showDialog" max-width="40%" persistent>
		<v-card flat>
			<v-card-title class="card-header text-left">
				<div class="font-size18">{{ $t('edit-medication') }} {{ $t('for') }} {{ prescription.patientFullName }}</div>
				<v-spacer />
				<v-icon @click="$emit('exitMedicationEditDialog')" color="white">mdi mdi-close</v-icon>
			</v-card-title>
			<v-card-text class="pa-4">
				<v-card class="pa-3">
					<v-row>
						<v-col cols="4" class="text-left">
							<v-icon>mdi-prescription</v-icon>
						</v-col>
						<v-col cols="2" />
						<v-col cols="6">
							<v-select
								outlined
								dense
								v-model="prescribingDoctor"
								:label="$t('prescribing-doctor')"
								:items="doctors"
								item-value="id"
								item-text="fullName"
								:menu-props="{ maxHeight: 200, offsetY: true }"
								hide-details />
						</v-col>
						<v-col cols="6" class="text-left">
							<v-text-field
								outlined
								dense
								v-model="prescription.brandName"
								:label="$t('brand-name')"
								hide-details
								disabled />
						</v-col>
						<v-col cols="6" class="text-right">
							<v-text-field
								outlined
								dense
								v-model="prescription.genericName"
								:label="$t('generic-name')"
								hide-details
								disabled />
						</v-col>
						<v-col cols="12">
							<v-text-field outlined dense v-model="dosage" :label="$t('dosage')" hide-details />
						</v-col>
						<v-col cols="4">
							<v-text-field outlined dense v-model="frequency" :label="$t('frequency')" hide-details />
						</v-col>
						<v-col cols="4">
							<v-text-field
								outlined
								dense
								v-model="period"
								:label="$t('period')"
								hide-details
								:suffix="$t('days')"
								@keypress="filterNumbersOnly" />
						</v-col>
						<v-col cols="4">
							<v-text-field outlined dense v-model="prescription.route" :label="$t('route')" hide-details disabled />
						</v-col>
						<v-col cols="12">
							<v-textarea
								outlined
								dense
								no-resize
								rows="8"
								v-model="additionalInfo"
								:label="$t('additional-information')"
								hide-details />
						</v-col>
						<v-col cols="4" class="text-left">
							<v-btn
								icon
								@click="deletePrescription(prescription.id)"
								:disabled="!prescription.perscriptionCanBeDeleted">
								<v-icon size="20" color="primary">mdi-delete</v-icon>
							</v-btn>
						</v-col>
						<v-col cols="4" v-if="prescription.perscriptionCanBeDeleted" class="red--text text--lighten-1">
							{{ $t('prescription-has-ended') }}
						</v-col>
						<v-col class="text-right"> {{ $t('On date') }}: {{ formatDate(prescription.prescribedAt) }} </v-col>
					</v-row>
				</v-card>
			</v-card-text>
			<v-card-actions class="mx-auto d-flex align-center justify-center">
				<v-row dense>
					<v-col cols="6">
						<v-btn color="secondary" block class="color-black" dense @click="$emit('exitMedicationEditDialog')">
							{{ $t('close') }}
						</v-btn>
					</v-col>
					<v-col cols="6">
						<v-btn
							block
							color="primary2"
							dense
							@click="updatePrescription"
							:disabled="prescription.perscriptionCanBeDeleted"
							class="text-color-white">
							{{ $t('confirm') }}
						</v-btn>
					</v-col>
				</v-row>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
import { formatterFilter } from '@/utils/luxon-formater';
import { mapState } from 'vuex';

export default {
	name: 'MedicationEditDialog',
	props: ['showDialog', 'prescription'],
	data () {
		return {
			dosage: 0,
			frequency: 0,
			period: 0,
			additionalInfo: '',
			prescribingDoctor: null,
			doctors: null,
		};
	},
	computed: {
		...mapState({
			detailedUser: (state) => state.users.detailedUser,
		}),
	},
	mounted () {
		this.doctors = this.filterDoctors();
		this.dosage = this.prescription.dosage;
		this.frequency = this.prescription.frequency;
		this.period = this.prescription.durationDays;
		this.prescribingDoctor = this.prescription.staffId;
		this.additionalInfo = this.prescription.additionalinfo;
	},
	methods: {
		async deletePrescription (prescriptionId) {
			try {
				await this.$store.dispatch('prescriptions/deletePrescription', prescriptionId);
				await this.getPrescriptionsForPatient();
			} catch (error) {
				// Change this to something more suitable
				this.$store.commit('alerts/add', {
					id: 'addedUser',
					type: 'error',
					color: 'main_red',
					message: this.$t('failed!'),
				});
			} finally {
				this.$emit('exitMedicationEditDialog');
			}
		},
		async getPrescriptionsForPatient () {
			try {
				await this.$store.dispatch('prescriptions/getPrescriptionsByPatientId', this.detailedUser.userData.id);
			} catch (error) {
				this.$store.commit('alerts/add', {
					id: 'addedUser',
					type: 'error',
					color: 'main_red',
					message: this.$t('failed!'),
				});
			}
		},
		filterDoctors () {
			return this.detailedUser.userData.medicalTeamsData[0].membersList
				.filter((member) => member.roleId === 3)
				.map((member) => ({
					...member,
					fullName: `${member.firstName} ${member.lastName}`,
				}));
		},
		filterNumbersOnly (inputEvent) {
			if (/\D/.test(inputEvent.key)) {
				inputEvent.preventDefault();
			}
			return true;
		},
		formatDate (val) {
			const dateObject = new Date(val);
			return formatterFilter(dateObject, 'date_med', this.timeLocalization, null);
		},
		async updatePrescription () {
			const body = {
				patientUserId: this.prescription.patientUserId,
				staffId: this.prescribingDoctor,
				hospitalId: this.prescription.hospitalId,
				dosage: this.dosage,
				frequency: this.frequency,
				additionalinfo: this.additionalInfo ?? '',
				brandName: this.prescription.brandName,
				genericName: this.prescription.genericName,
				productNdc: this.prescription.productNdc,
				manufacturerName: this.prescription.manufacturerName,
				route: this.prescription.route,
				warnings: this.prescription.warnings ?? '',
				adverseReactions: this.prescription.adverseReactions ?? '',
				durationDays: Number(this.period),
				prescribedAt: this.prescription.prescribedAt,
			};

			try {
				const request = {
					body: body,
					prescriptionId: this.prescription.id,
				};

				await this.$store.dispatch('prescriptions/updatePrescription', request);
				await this.getPrescriptionsForPatient();
			} catch (error) {
				// Change this to something more suitable
				this.$store.commit('alerts/add', {
					id: 'updatedPrescription',
					type: 'error',
					color: 'main_red',
					message: this.$t('failed!'),
				});
			} finally {
				this.$emit('exitMedicationEditDialog');
			}
		},
	},
};
</script>
